import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { Row, Col, Card, Typography } from 'antd';
import { Link } from 'react-router-dom';
import './Css/DashboardComponent.css';
import { formatTitle } from '../utils/formatTitle';
import api from '../api/axiosConfig';

const { Title } = Typography;

const DashboardComponent = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resultados, setResultados] = useState([]);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const user = JSON.parse(userData);
        setProfile(user.profile);
      } catch (err) {
        setProfile(null);
      }
    }
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchDashboardData = async () => {
      try {
        const response = await api.get('/dashboard');
        if (isMounted) {
          setDashboardData(response.data);
          setLoading(false);
        }
      } catch (err) {
        if (isMounted) {
          setLoading(false);
        }

        Swal.fire({
          title: 'Erro!',
          text: 'Não foi possível carregar os dados do dashboard. Faça login novamente.',
          icon: 'error',
          confirmButtonText: 'OK'
        }).then(() => {
          window.location.href = "/login";
        });
      }
    };

    const fetchResultados = async () => {
      try {
        const resultResponse = await api.get('/resultados');
        if (isMounted) {
          if (resultResponse.data && resultResponse.data.data) {
            setResultados(resultResponse.data.data);
          } else {
            console.error('Resultado não encontrado na resposta');
          }
        }
      } catch (err) {
        console.error('Erro ao carregar os resultados:', err);
        setResultados([]);
      }
    };

    if (loading) {
      Swal.fire({
        title: 'Carregando...',
        text: 'Estamos processando as informações.',
        icon: 'info',
        showConfirmButton: false,
        willOpen: () => {
          Swal.showLoading();
        }
      });
    }

    fetchDashboardData();
    fetchResultados();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!loading) {
      setTimeout(() => {
        Swal.close();
      }, 1500);
    }
  }, [loading]);

  if (error) {
    Swal.fire({
      title: 'Erro!',
      text: 'Não foi possível carregar os dados do dashboard.',
      icon: 'error',
      confirmButtonText: 'OK'
    });
    return <div>{error}</div>;
  }

  // Função para renderizar o card de análise
  const renderCard = (title, value) => {
    let cardValue;

    const valuesMap = {
      colValues: ['c1', 'c2', 'c3', 'c4', 'c5'],
      liValues: ['li1', 'li2', 'li3', 'li4', 'li5'],
      lpValues: ['lp1', 'lp2', 'lp3', 'lp4', 'lp5'],
      moldurasValues: ['externa', 'interna'],
      parImparValues: ['par', 'impar'],
      linhaValues: ['l1', 'l2', 'l3', 'l4', 'l5'],
    };

    let usedValues = new Set();

    cardValue = Object.keys(valuesMap).map(key => {
      const values = valuesMap[key]
        .map(k => value[k])
        .filter(v => v != null && !usedValues.has(v))
        .map(v => {
          usedValues.add(v);
          return v;
        })
        .join('  ');

      if (values) return values;
      return null;
    }).filter(Boolean).join(', ');

    if (!cardValue) {
      cardValue = '0';
    }

    cardValue =
      value.asteriscos ||
      value.coringa ||
      value.sequencias ||
      value.repetidos ||
      value.soma ||
      value.triangular ||
      value.dezenas ||
      value.fibonacci ||
      value.mult3Perf ||
      value.multiplo ||
      value.fiboCm ||
      value.magico ||
      value.corMag ||
      value.misticos ||
      value.perfeito ||
      value.primoCm ||
      value.primos ||
      cardValue;

    // 🔹 Correções: Molduras e Par/Ímpar (normal + CC)
    if ((title === 'analise_molduras' || title === 'analise_molduras_CC') &&
      value.externa != null && value.interna != null) {
      cardValue = `${value.externa} | ${value.interna}`;
    } else if (
      (title === 'analise_par_impar' || title === 'analise_par_impar_CC') &&
      value.par != null && value.impar != null
    ) {
      cardValue = `${value.par} | ${value.impar}`;
    }

    cardValue = String(cardValue);

    let shapeClass = 'circle';
    let triangleText = null;

    if (cardValue.includes(' ')) {
      const values = cardValue.split(' ');
      if (values.length > 2) {
        shapeClass = 'rectangle';
      }
    } else if (cardValue.length === 1) {
      shapeClass = 'triangle';
      triangleText = cardValue;
    }

    return (
      <Col xs={24} sm={12} md={8} lg={6} style={{ marginBottom: '20px' }} key={title}>
        <Link to={`/pagina/${title}`}>
          <Card
            title={
              <div style={{ textAlign: 'center', color: 'white' }}>
                {formatTitle(title)}
              </div>
            }
            style={{
              backgroundColor: '#2AA6B9',
              color: 'white',
              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
              borderRadius: '8px',
              margin: '0 auto',
              transition: 'transform 0.3s ease, background-color 0.3s ease',
            }}
            className="card-hover"
          >
            <div className={`shape ${shapeClass}`}>
              {shapeClass === 'triangle' && triangleText && (
                <div className="triangle-text">{triangleText}</div>
              )}
              {shapeClass !== 'triangle' && cardValue}
            </div>
          </Card>
        </Link>
      </Col>
    );
  };

  // Função para gerar a grade de números
  const generateNumberGrid = () => {
    const numbers = [];
    for (let i = 1; i <= 25; i++) {
      numbers.push(i < 10 ? `0${i}` : `${i}`);
    }
    let grid = [];
    for (let i = 0; i < 5; i++) {
      grid.push(numbers.slice(i * 5, i * 5 + 5));
    }
    return grid;
  };

  const markWinningNumbers = (numbers) => {
    return generateNumberGrid().map((row, index) => (
      <div key={index} className="number-row">
        {row.map((num, idx) => {
          const isWinningNumber = numbers.includes(Number(num));
          return (
            <div
              key={idx}
              className={`number-item ${isWinningNumber ? 'winning' : ''}`}
            >
              {num}
            </div>
          );
        })}
      </div>
    ));
  };

  const cardsPadrao = [
    'analise_magico_coringa', 'analise_mult3_perf_cm', 'analise_fibonacci_cm', 'analise_primo_cm',
    'analise_linhas', 'analise_colunas', 'analise_linhas_impar', 'analise_linhas_pares',
    'analise_multiplo3', 'analise_fibonacci', 'analise_primos', 'analise_triangular',
    'analise_perfeito', 'analise_repetidos', 'analise_sequencia', 'analise_asteriscos',
    'analise_inicio', 'analise_final', 'analise_magico', 'analise_soma',
    'analise_coringa', 'analise_misticos', 'analise_molduras', 'analise_par_impar'
  ];

  const cardsAdmin = [
    'analise_magico_coringa_CC',
    'analise_mix3xPerf_CC',
    'analise_fiboCm_CC',
    'analise_prioCm_CC',
    'analise_soma_CC',
    'analise_asteriscos_CC',
    'analise_triangulares_CC',
    'analise_numerosPerfeitos_CC',
    'analise_coringa_CC',
    'analise_fibonacci_cc',
    'analise_multiplo3_CC',
    'analise_primos_CC',
    'analise_magicos_CC',
    'analise_par_impar_CC',
    'analise_molduras_CC',
  ];

  const cardsExibir = profile === 'admin'
    ? [...cardsPadrao, ...cardsAdmin]
    : cardsPadrao;

  return (
    <div className="dashboard-container">
      <div className="number-grid-container">
        {resultados.length > 0 && (
          <>
            {resultados.length > 0 && (
              <div className="number-grid">
                <div className="lotofacil-title-wrapper">
                  <div className="lotofacil-title">Lotofácil - {resultados[0].id}</div>
                </div>
                {markWinningNumbers(resultados[0].numeros || [])}
              </div>
            )}
            {resultados.length > 1 && (
              <div className="number-grid">
                <div className="lotofacil-title-wrapper">
                  <div className="lotofacil-title">Lotofácil - {resultados[1].id}</div>
                </div>
                {markWinningNumbers(resultados[1].numeros || [])}
              </div>
            )}
          </>
        )}
      </div>

      <Row gutter={[16, 16]}>
        {dashboardData &&
          cardsExibir.map((key) => {
            const value = dashboardData[key];
            return renderCard(key, value || {});
          })
        }
      </Row>
    </div>
  );
};

export default DashboardComponent;
